import { useState, useCallback, useRef, useEffect } from 'react';
import { Bot, SearchFilters } from '@/types/Bot';
import { supabase } from '@/integrations/supabase/client';
import { useAnalytics } from '@/hooks/useAnalytics';
import { LRUCache } from '@/lib/lru';
import { AI_CONFIG, getLatencyBucket } from '@/config/ai';
import { validateSmartSortResponse, validateSmartSortError, SmartSortResponse } from '@/schemas/smartSort';
import { useAuth } from '@/contexts/AuthContext';

interface UseSmartSortProps {
  intent: string;
  filters: SearchFilters;
  visibleItems: Bot[];
  enabled: boolean;
}

interface SmartSortState {
  isLoading: boolean;
  orderedIds: string[];
  scores?: Record<string, number>;
  scoringNotes?: string;
  usedAiFallback: boolean;
  sessionDisabled: boolean;
  lastRequestId?: string;
}

// Global cache instance
const smartSortCache = new LRUCache<SmartSortResponse>(AI_CONFIG.CACHE_MAX_SIZE, AI_CONFIG.TTL_MS);

// Session failure tracking
let sessionFailureCount = 0;
let sessionDisabled = false;

// Simple browser-compatible hash function
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

// Simple browser-compatible UUID generator
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const useSmartSort = ({ intent, filters, visibleItems, enabled }: UseSmartSortProps) => {
  const { trackEvent } = useAnalytics();
  const { isPremium } = useAuth();
  const [state, setState] = useState<SmartSortState>({
    isLoading: false,
    orderedIds: [],
    usedAiFallback: false,
    sessionDisabled: false
  });
  
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Build stable cache key
  const buildCacheKey = useCallback((intent: string, filters: SearchFilters, visibleIds: string[]): string => {
    const normalizedFilters = Object.fromEntries(
      Object.entries(filters)
        .filter(([_, value]) => value !== '' && value !== 'all' && (!Array.isArray(value) || value.length > 0))
        .sort(([a], [b]) => a.localeCompare(b))
    );
    
    const sortedIds = [...visibleIds].sort();
    const payload = {
      intent: intent.trim(),
      filters: normalizedFilters,
      ids: sortedIds,
      version: AI_CONFIG.SMART_SORT_VERSION
    };
    
    return simpleHash(JSON.stringify(payload));
  }, []);

  // Merge ordered IDs with filtered items
  const mergeResults = useCallback((filteredItems: Bot[], orderedIds: string[]): Bot[] => {
    const byId = new Map(filteredItems.map(item => [item.id, item]));
    const placed = new Set<string>();
    const merged: Bot[] = [];

    // Place items in order from AI response
    for (const id of orderedIds) {
      if (byId.has(id) && !placed.has(id)) {
        merged.push(byId.get(id)!);
        placed.add(id);
      }
    }

    // Append remaining items in original order
    for (const item of filteredItems) {
      if (!placed.has(item.id)) {
        merged.push(item);
      }
    }

    return merged;
  }, []);

  // Call Vergo AI function with retry logic and timeout
  const callSmartSort = useCallback(async (
    intent: string,
    filters: SearchFilters,
    catalog: Bot[],
    requestId: string
  ): Promise<SmartSortResponse | null> => {
    const startTime = Date.now();
    
    // Prepare lean catalog data
    const leanCatalog = catalog.slice(0, AI_CONFIG.SLICE_MAX).map(bot => ({
      id: bot.id,
      name: bot.name,
      short_description: bot.description.substring(0, AI_CONFIG.DESCRIPTION_TRUNCATE),
      category: bot.category,
      tags: bot.tags,
      price: bot.price,
      rating: bot.rating,
      preview_url: bot.demoUrl || null,
      site_url: bot.website
    }));

    const payload = {
      task_type: 'smart_sort',
      intent: intent.trim(),
      filters: filters,
      catalog: leanCatalog
    };

    for (let attempt = 0; attempt <= AI_CONFIG.RETRY_COUNT; attempt++) {
      try {
        // Create abort controller for timeout
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), AI_CONFIG.AI_REQUEST_TIMEOUT_MS);
        
        const { data, error } = await supabase.functions.invoke('vergo-ai', {
          body: payload,
          headers: {
            'Content-Type': 'application/json'
          }
        });

        clearTimeout(timeoutId);
        const latencyMs = Date.now() - startTime;

        if (error) {
          // Check if this is a retryable error
          const errorMessage = error.message?.toLowerCase() || '';
          if (attempt < AI_CONFIG.RETRY_COUNT && (
            errorMessage.includes('rate limit') ||
            errorMessage.includes('timeout') ||
            errorMessage.includes('network') ||
            errorMessage.includes('503') ||
            errorMessage.includes('502')
          )) {
            const backoffMs = AI_CONFIG.BACKOFF_BASE_MS * Math.pow(2, attempt);
            console.log(`Retrying smart sort in ${backoffMs}ms (attempt ${attempt + 1}/${AI_CONFIG.RETRY_COUNT + 1})`);
            await new Promise(resolve => setTimeout(resolve, backoffMs));
            continue;
          }
          throw new Error(`Edge function error: ${error.message}`);
        }

        // Validate response
        const validatedResponse = validateSmartSortResponse(data);
        if (!validatedResponse) {
          // Check if it's an error response
          const validatedError = validateSmartSortError(data);
          if (validatedError) {
            throw new Error(`AI Error: ${validatedError.error.code} - ${validatedError.error.message}`);
          }
          throw new Error('Invalid response schema');
        }

        // Track success
        trackEvent('ai_sort_succeeded', {
          request_id: requestId,
          smartSortVersion: AI_CONFIG.SMART_SORT_VERSION,
          catalog_count: catalog.length,
          visible_count: leanCatalog.length,
          latency_ms: latencyMs,
          latency_bucket: getLatencyBucket(latencyMs),
          result_count: validatedResponse.ordered_ids.length,
          retry_attempt: attempt
        });

        return validatedResponse;
        
      } catch (error: any) {
        const latencyMs = Date.now() - startTime;
        
        // Handle timeout specifically
        if (error.name === 'AbortError') {
          if (attempt < AI_CONFIG.RETRY_COUNT) {
            const backoffMs = AI_CONFIG.BACKOFF_BASE_MS * Math.pow(2, attempt);
            console.log(`Request timed out, retrying in ${backoffMs}ms (attempt ${attempt + 1}/${AI_CONFIG.RETRY_COUNT + 1})`);
            await new Promise(resolve => setTimeout(resolve, backoffMs));
            continue;
          }
          
          trackEvent('ai_sort_failed', {
            request_id: requestId,
            error_type: 'timeout',
            latency_ms: latencyMs,
            catalog_count: catalog.length,
            retry_attempt: attempt
          });
          
          throw new Error('Request timeout');
        }
        
        // If this is the last attempt, track and throw
        if (attempt === AI_CONFIG.RETRY_COUNT) {
          trackEvent('ai_sort_failed', {
            request_id: requestId,
            error_type: error instanceof Error ? error.message : 'unknown',
            latency_ms: latencyMs,
            catalog_count: catalog.length,
            retry_attempt: attempt
          });
          
          throw error;
        }
        
        // Check if error is retryable
        const errorMessage = error.message?.toLowerCase() || '';
        if (errorMessage.includes('rate limit') || 
            errorMessage.includes('timeout') ||
            errorMessage.includes('network') ||
            errorMessage.includes('503') ||
            errorMessage.includes('502')) {
          const backoffMs = AI_CONFIG.BACKOFF_BASE_MS * Math.pow(2, attempt);
          console.log(`Retrying smart sort in ${backoffMs}ms (attempt ${attempt + 1}/${AI_CONFIG.RETRY_COUNT + 1})`);
          await new Promise(resolve => setTimeout(resolve, backoffMs));
          continue;
        }
        
        // Non-retryable error, track and throw immediately
        trackEvent('ai_sort_failed', {
          request_id: requestId,
          error_type: error instanceof Error ? error.message : 'unknown',
          latency_ms: latencyMs,
          catalog_count: catalog.length,
          retry_attempt: attempt
        });
        
        throw error;
      }
    }
    
    // This should never be reached, but TypeScript needs it
    throw new Error('Max retries exceeded');
  }, [trackEvent]);

  // Smart sort with retry logic
  const performSmartSort = useCallback(async (): Promise<void> => {
    if (!enabled || !isPremium || visibleItems.length < AI_CONFIG.MIN_RESULTS || sessionDisabled) {
      return;
    }

    const requestId = generateUUID();
    const cacheKey = buildCacheKey(intent, filters, visibleItems.map(item => item.id));

    // Check cache first
    const cached = smartSortCache.get(cacheKey);
    if (cached) {
      setState(prev => ({
        ...prev,
        orderedIds: cached.ordered_ids,
        scores: cached.scores,
        scoringNotes: cached.scoring_notes,
        usedAiFallback: false,
        lastRequestId: cached.request_id
      }));
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setState(prev => ({ ...prev, isLoading: true }));

    trackEvent('ai_sort_requested', {
      request_id: requestId,
      catalog_count: visibleItems.length,
      intent_hash: simpleHash(intent.trim())
    });

    try {
      const result = await callSmartSort(intent, filters, visibleItems, requestId);
      
      if (result) {
        // Cache the result
        smartSortCache.set(cacheKey, result);
        
        setState(prev => ({
          ...prev,
          isLoading: false,
          orderedIds: result.ordered_ids,
          scores: result.scores,
          scoringNotes: result.scoring_notes,
          usedAiFallback: false,
          lastRequestId: result.request_id
        }));

        // Reset session failure count on success
        sessionFailureCount = 0;
      }
    } catch (error) {
      console.error('Smart sort failed:', error);
      
      // Increment failure count
      sessionFailureCount++;
      
      // Auto-disable if too many failures
      if (sessionFailureCount >= AI_CONFIG.FAILURE_AUTO_DISABLE_AFTER) {
        sessionDisabled = true;
        setState(prev => ({ ...prev, sessionDisabled: true }));
      }

      trackEvent('ai_sort_fallback', {
        request_id: requestId,
        fallback_reason: error instanceof Error ? error.message : 'unknown',
        failure_count: sessionFailureCount
      });

      setState(prev => ({
        ...prev,
        isLoading: false,
        usedAiFallback: true
      }));
    }
  }, [enabled, isPremium, visibleItems, intent, filters, buildCacheKey, callSmartSort, trackEvent]);

  // Debounced smart sort
  const debouncedSmartSort = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      performSmartSort();
    }, AI_CONFIG.DEBOUNCE_MS);
  }, [performSmartSort]);

  // Trigger smart sort when inputs change
  useEffect(() => {
    if (enabled && isPremium && visibleItems.length >= AI_CONFIG.MIN_RESULTS && !sessionDisabled) {
      debouncedSmartSort();
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [enabled, isPremium, intent, filters, visibleItems.length, debouncedSmartSort, sessionDisabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Replay function for dev mode
  const replayLastRequest = useCallback(() => {
    if (!state.lastRequestId || !visibleItems.length) return;
    
    // Clear cache and force a new request
    smartSortCache.clear();
    performSmartSort();
  }, [state.lastRequestId, visibleItems, performSmartSort]);

  // Get merged results
  const mergedResults = state.orderedIds.length > 0 ? mergeResults(visibleItems, state.orderedIds) : visibleItems;

  return {
    mergedResults,
    isLoading: state.isLoading,
    scores: state.scores,
    scoringNotes: state.scoringNotes,
    usedAiFallback: state.usedAiFallback,
    sessionDisabled: state.sessionDisabled,
    lastRequestId: state.lastRequestId,
    replayLastRequest
  };
};